/**
 * JWT utility functions for parsing and extracting claims
 */

export interface JWTPayload {
  sub?: string
  email?: string
  'custom:role'?: string
  'custom:tenantId'?: string
  exp?: number
  iat?: number
  [key: string]: any
}

/**
 * Decodes a JWT token and returns the payload
 * @param token - The JWT token string
 * @returns The decoded payload or null if invalid
 */
export function decodeJWT(token: string): JWTPayload | null {
  try {
    // JWT format: header.payload.signature
    const parts = token.split('.')
    if (parts.length !== 3) {
      return null
    }

    // Decode the payload (second part)
    const payload = parts[1]
    
    // Base64 URL decode
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/')
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    )

    return JSON.parse(jsonPayload) as JWTPayload
  } catch (error) {
    console.error('Error decoding JWT:', error)
    return null
  }
}

/**
 * Extracts the custom:role claim from a JWT token
 * @param token - The JWT token string
 * @returns The role value or null if not present
 */
export function extractRoleFromToken(token: string): string | null {
  const payload = decodeJWT(token)
  return payload?.['custom:role'] || null
}

/**
 * Checks if a JWT token contains the platform_admin role
 * @param token - The JWT token string
 * @returns True if the token contains platform_admin role
 */
export function isPlatformAdmin(token: string): boolean {
  const role = extractRoleFromToken(token)
  return role === 'platform_admin'
}

/**
 * Extracts the tenant ID from a JWT token
 * @param token - The JWT token string
 * @returns The tenant ID or null if not present
 */
export function extractTenantIdFromToken(token: string): string | null {
  const payload = decodeJWT(token)
  return payload?.['custom:tenantId'] || null
}

/**
 * Extracts the custom:userType claim from a JWT token
 * @param token - The JWT token string
 * @returns The userType value or null if not present
 */
export function extractUserTypeFromToken(token: string): string | null {
  const payload = decodeJWT(token)
  return payload?.['custom:userType'] || null
}
